import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../libs/axios';
import Breadcrumb from '../../utils/breadcrumbs';
import { ListLoader } from '../../utils/loader';
import MapPicker from './map-picker';

interface MatchGround {
  id: number;
  name: string;
  city: string | null;
  lat: string | null;
  long: string | null;
  is_active: boolean;
  match_count: number;
  tournament_count: number;
  // Null for grounds added before the backend started recording it.
  created_by_name: string | null;
}

interface DuplicateGround {
  id: number;
  name: string;
  city: string | null;
  lat: string | null;
  long: string | null;
  is_active?: boolean;
  match_count?: number;
}
interface Duplicates {
  by_name: Array<{ key: string; grounds: DuplicateGround[] }>;
  by_location: Array<{ a: DuplicateGround; b: DuplicateGround }>;
}

const num = (v: string | null): number | null => {
  if (v == null || String(v).trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const hasLocation = (g: { lat: string | null; long: string | null }) => {
  const la = num(g.lat), lo = num(g.long);
  return la != null && lo != null && !(la === 0 && lo === 0);
};

const MatchGrounds: React.FC = () => {
  const [rows, setRows] = useState<MatchGround[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [state, setState] = useState('');
  const [missingOnly, setMissingOnly] = useState(false);

  // Edit drawer
  const [editing, setEditing] = useState<MatchGround | null>(null);
  const [form, setForm] = useState({ name: '', city: '', lat: '', long: '', is_active: true });
  const [saving, setSaving] = useState(false);

  // Merge
  const [dupes, setDupes] = useState<Duplicates | null>(null);
  const [dupesOpen, setDupesOpen] = useState(false);
  const [mergeFrom, setMergeFrom] = useState<MatchGround | null>(null);
  const [mergeIntoId, setMergeIntoId] = useState('');

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 20 };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (state) params.state = state;
      if (missingOnly) params.missing_location = 'true';
      const res: any = await api.get('/match-grounds', { params });
      const payload = res.data || {};
      setRows(payload.data || []);
      setTotalPages(payload.pagination?.total_pages || 1);
      setTotal(payload.pagination?.total ?? 0);
    } catch (e) {
      toast.error('Failed to load grounds');
    } finally { setLoading(false); }
  }, [page, debouncedSearch, state, missingOnly]);

  useEffect(() => { load(); }, [load]);

  const loadDupes = async () => {
    try {
      const res: any = await api.get('/match-grounds/duplicates');
      setDupes(res.data || null);
      setDupesOpen(true);
    } catch (e) { toast.error('Failed to check for duplicates'); }
  };

  const openEdit = (g: MatchGround) => {
    setEditing(g);
    setForm({
      name: g.name || '',
      city: g.city || '',
      lat: g.lat || '',
      long: g.long || '',
      is_active: g.is_active,
    });
  };

  const save = async () => {
    if (!editing) return;
    if (form.name.trim().length < 2) { toast.error('Name must be at least 2 characters'); return; }
    setSaving(true);
    try {
      const res: any = await api.put(`/match-grounds/${editing.id}`, {
        name: form.name.trim(),
        city: form.city.trim(),
        lat: form.lat.trim(),
        long: form.long.trim(),
        is_active: form.is_active,
      });
      const moved = res?.data?.matches_relocated ?? 0;
      toast.success(moved > 0 ? `Saved — ${moved} match(es) moved to the new location` : 'Saved');
      setEditing(null);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const remove = async (g: MatchGround) => {
    if (!window.confirm(`Delete "${g.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/match-grounds/${g.id}`);
      toast.success('Ground deleted');
      load();
    } catch (e: any) {
      // The backend refuses while matches still point at it, and says how many.
      toast.error(e?.response?.data?.message || 'Failed to delete', { duration: 6000 });
    }
  };

  const doMerge = async () => {
    if (!mergeFrom || !mergeIntoId) return;
    const into = rows.find((r) => String(r.id) === mergeIntoId);
    if (!window.confirm(
      `Merge "${mergeFrom.name}" into "${into?.name ?? '#' + mergeIntoId}"?\n\n` +
      `${mergeFrom.match_count} match(es) and ${mergeFrom.tournament_count} tournament(s) will move across, ` +
      `and "${mergeFrom.name}" will be deleted. This cannot be undone.`
    )) return;
    try {
      const res: any = await api.post('/match-grounds/merge', {
        from_id: mergeFrom.id,
        into_id: Number(mergeIntoId),
      });
      toast.success(res?.message || 'Merged');
      setMergeFrom(null);
      setMergeIntoId('');
      load();
      if (dupesOpen) loadDupes();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Merge failed');
    }
  };

  const dupeCount = (dupes?.by_name.length ?? 0) + (dupes?.by_location.length ?? 0);

  return (
    <div className="p-4 sm:p-6">
      <Breadcrumb />
      <div className="mt-4 mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Match Grounds</h1>
          <p className="text-sm text-gray-500">
            The grounds every match and tournament points at — added from the app during match creation.
            Fixing a location here also corrects it on that ground's existing matches.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or city…" className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-56" />
          <select value={state} onChange={(e) => { setPage(1); setState(e.target.value); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Hidden</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
            <input type="checkbox" checked={missingOnly}
              onChange={(e) => { setPage(1); setMissingOnly(e.target.checked); }} />
            Needs a location
          </label>
          <button onClick={loadDupes} className="text-sm font-medium px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50">
            Find duplicates
          </button>
          <button onClick={load} className="text-sm font-medium px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50">Refresh</button>
        </div>
      </div>

      {/* Duplicates */}
      {dupesOpen && dupes && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-gray-800">
              Possible duplicates {dupeCount > 0 && <span className="text-gray-400">({dupeCount})</span>}
            </div>
            <button onClick={() => setDupesOpen(false)} className="text-xs text-gray-500 hover:text-gray-700">Close</button>
          </div>
          {dupeCount === 0 ? (
            <p className="text-sm text-gray-500">Nothing looks duplicated — same-name and same-location checks both came back clean.</p>
          ) : (
            <div className="space-y-3">
              {dupes.by_name.map((grp) => (
                <div key={grp.key} className="text-sm">
                  <span className="text-gray-500 text-xs">Same name:</span>{' '}
                  {grp.grounds.map((g) => (
                    <span key={g.id} className="inline-block mr-2 text-gray-800">
                      {g.name} <span className="text-gray-400">#{g.id} · {g.match_count ?? 0} matches</span>
                    </span>
                  ))}
                </div>
              ))}
              {dupes.by_location.map((p, i) => (
                <div key={i} className="text-sm">
                  <span className="text-gray-500 text-xs">Within ~200m:</span>{' '}
                  <span className="text-gray-800">{p.a.name} <span className="text-gray-400">#{p.a.id}</span></span>
                  <span className="text-gray-400"> &amp; </span>
                  <span className="text-gray-800">{p.b.name} <span className="text-gray-400">#{p.b.id}</span></span>
                </div>
              ))}
              <p className="text-xs text-gray-400 pt-1">
                Use "Merge" on a row below to fold one into another — its matches move across first.
              </p>
            </div>
          )}
        </div>
      )}

      {loading ? <ListLoader /> : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left font-medium px-4 py-3">Ground</th>
                  <th className="text-left font-medium px-4 py-3">City</th>
                  <th className="text-left font-medium px-4 py-3">Location</th>
                  <th className="text-left font-medium px-4 py-3">Matches</th>
                  <th className="text-left font-medium px-4 py-3">Tournaments</th>
                  <th className="text-left font-medium px-4 py-3">Added by</th>
                  <th className="text-left font-medium px-4 py-3">Shown in app</th>
                  <th className="text-left font-medium px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No grounds</td></tr>
                ) : rows.map((g) => (
                  <tr key={g.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 text-gray-900">{g.name} <span className="text-gray-400">#{g.id}</span></td>
                    <td className="px-4 py-3 text-gray-600">{g.city || <span className="text-gray-400">—</span>}</td>
                    <td className="px-4 py-3">
                      {hasLocation(g) ? (
                        <a href={`https://www.google.com/maps?q=${g.lat},${g.long}`} target="_blank" rel="noreferrer"
                          className="text-primary-600 hover:underline text-xs">
                          {Number(g.lat).toFixed(4)}, {Number(g.long).toFixed(4)}
                        </a>
                      ) : (
                        <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">no location</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{g.match_count}</td>
                    <td className="px-4 py-3 text-gray-700">{g.tournament_count}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {g.created_by_name || <span className="text-gray-300" title="Added before this was recorded">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {g.is_active
                        ? <span className="text-green-600 text-xs font-medium">yes</span>
                        : <span className="text-gray-400 text-xs">hidden</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(g)}
                          className="text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50">Edit</button>
                        <button onClick={() => { setMergeFrom(g); setMergeIntoId(''); }}
                          className="text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50">Merge</button>
                        <button onClick={() => remove(g)}
                          className="text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-300 text-red-600 hover:bg-red-50">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-50">Prev</button>
            <span className="text-gray-500">Page {page} of {totalPages}{total ? ` · ${total} grounds` : ''}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      {/* Edit */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-2xl p-5 my-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Edit ground</h2>
              <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
                <input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </div>
            </div>

            <MapPicker
              lat={num(form.lat)}
              lng={num(form.long)}
              onChange={(la, lo) => setForm((f) => ({ ...f, lat: la.toFixed(6), long: lo.toFixed(6) }))}
            />

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Latitude</label>
                <input type="text" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Longitude</label>
                <input type="text" value={form.long} onChange={(e) => setForm({ ...form, long: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700 mt-4 select-none">
              <input type="checkbox" checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Shown in the app when creating a match
            </label>

            {editing.match_count > 0 && (
              <p className="text-xs text-gray-500 mt-4">
                Changing the location also updates it on this ground's {editing.match_count} existing match(es).
              </p>
            )}

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(null)}
                className="text-sm font-medium px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50">Cancel</button>
              <button onClick={save} disabled={saving}
                className="text-sm font-medium px-5 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merge */}
      {mergeFrom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Merge ground</h2>
            <p className="text-sm text-gray-500 mb-4">
              <span className="font-medium text-gray-700">{mergeFrom.name}</span> will be deleted, and its{' '}
              {mergeFrom.match_count} match(es) and {mergeFrom.tournament_count} tournament(s) will move to the ground you pick.
            </p>
            <label className="block text-xs font-medium text-gray-600 mb-1">Keep this ground</label>
            <select value={mergeIntoId} onChange={(e) => setMergeIntoId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="">Select a ground…</option>
              {rows.filter((r) => r.id !== mergeFrom.id).map((r) => (
                <option key={r.id} value={r.id}>{r.name}{r.city ? ` — ${r.city}` : ''} ({r.match_count} matches)</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-2">
              Only grounds on this page are listed — search first if the one you want isn't here.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setMergeFrom(null)}
                className="text-sm font-medium px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50">Cancel</button>
              <button onClick={doMerge} disabled={!mergeIntoId}
                className="text-sm font-medium px-5 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                Merge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchGrounds;
